import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { Main, MainBody, MainTop, MainTopTitle, WhiteBox } from '@/components/Layout'
import Resx from '@/components/Resx'
import { NumericInput } from '@/components/inputs'
import { Row, Col, FormGroup, Label } from 'reactstrap'
import context from '@/context'
import { useDispatch } from '@/hooks/dispatch'
import { actionCreators } from '@/store/Dialogs'
import { dialogTypes } from '@/dialogs'
import LoadingIndicator from '@/components/LoadingIndicator'
import { Button } from '@/components/button'
import { GridColumn as Column } from '@progress/kendo-react-grid'
import { BsrsGrid, commandColumnProps, LookupNameColumn } from '@/components/BsrsGrid'
import { useLocalization } from '@/hooks/localization'
import { openAlertDialog, openConfirmDialog, openErrorDialog } from '@/utils/dialogHelpers'
import { MultiSelect } from '@/components/MultiSelect'
import cached from '@/internal-app/cached'
import { useBackgroundJobTracking } from '@/hooks/useBackgroundJobRunning'

export const DataCrossingSettings = function DataCrossingSettings (props) {
  const primaryClass = 'btn-primary'
  const errorClass = 'btn-danger'
  const successClass = 'btn-success'
  const loadingIcon = 'bx-loader bx-spin'
  const successIcon = 'check'
  const errorIcon = 'x-circle'
  const { localize, locale } = useLocalization()

  const [loadingThreshold, setLoadingThreshold] = useState(false)
  const [threshold, setThreshold] = useState(null)
  const [buttonStyle, setButtonStyle] = useState({ class: primaryClass, icon: '' })
  const [lastReload, setLastReload] = useState(new Date())
  const [weightsUnlocked, setWeightsUnlocked] = useState(false)
  const [allowedCombinationLastReload, setAllowedCombinationLastReload] = useState(new Date())
  const [allowedCombinationsUnlocked, setAllowedCombinationsUnlocked] = useState(false)
  const [loadingBankStates, setLoadingBankStates] = useState(false)
  const [allowedBankStates, setAllowedBankStates] = useState([])

  const [openDialog] = useDispatch([
    actionCreators.openDialog
  ])

  useEffect(() => {
    setLoadingThreshold(true)
    setLoadingBankStates(true)
    context.api.dataCrossingService.getSettings()
      .then((response) => {
        setThreshold(response.threshold / 100)
        setLoadingThreshold(false)
      }).catch(() => {
        openDialog({
          dialogType: dialogTypes.alert,
          title: 'DataCrossings.LoadingError',
          content: 'DataCrossings.SettingsLoadingError'
        })
      })
    context.api.dataCrossingService.getAllowedBankStates()
      .then(res => {
        setAllowedBankStates(res)
        setLoadingBankStates(false)
      })
      .catch(() => {
        openDialog({
          dialogType: dialogTypes.alert,
          title: 'DataCrossings.LoadingError',
          content: 'DataCrossings.SettingsLoadingError'
        })
      })
  }, [openDialog])

  const { running: normalizationRunning, start: triggerNormalization } = useBackgroundJobTracking(
    context.api.dataCrossingService.triggerNormalizationJob
  )
  const { running: checkCombinationsRunning, start: checkCombinations } = useBackgroundJobTracking(
    context.api.dataCrossingService.checkProblematicCombinations
  )
  const { running: duplicitiesRunning, start: checkDuplicities } = useBackgroundJobTracking(
    context.api.dataCrossingService.triggerDuplicatesProcedure
  )

  const onSaveClick = useCallback(() => {
    setButtonStyle({ class: primaryClass, icon: loadingIcon })
    context.api.dataCrossingService.setSettings({ threshold: (threshold * 100).toFixed(0) })
      .then(() => {
        setButtonStyle({ class: successClass, icon: successIcon })
      })
      .catch(() => {
        setButtonStyle({ class: errorClass, icon: errorIcon })
      })
  }, [threshold, setButtonStyle])

  const onDuplicatesClick = useCallback(e => {
    setWeightsUnlocked(false)
    checkDuplicities()
      .then(x => openAlertDialog({
        title: 'DataCrossings.DuplicateProcedureStartedTitle',
        text: 'DataCrossings.DuplicateProcedureStarted'
      })).catch(openErrorDialog)
  }, [checkDuplicities])

  const onNormalizationClick = useCallback(e => {
    triggerNormalization()
      .then(_ => openAlertDialog({
        title: 'DataCrossings.NormalizationProcedureStartedTitle',
        text: 'DataCrossings.NormalizationProcedureStarted'
      })).catch(openErrorDialog)
  }, [triggerNormalization])

  const onCheckCombinationsClick = useCallback(() => {
    checkCombinations()
      .then(x => openAlertDialog({
        title: 'DataCrossings.CheckProblematicCombinationsStartedTitle',
        text: 'DataCrossings.CheckProblematicCombinationsStarted'
      })).catch(openErrorDialog)
  }, [checkCombinations])

  const editWeight = useCallback((attributeName, weight) => {
    context.api.dataCrossingService.setWeight({ attributeName, weight })
      .then(() => {
        setLastReload(new Date())
      }).catch(() => {
        openDialog({
          dialogType: dialogTypes.alert,
          title: 'DataCrossings.SettingWeightErrorTitle',
          content: 'DataCrossings.SettingWeightError'
        })
      })
  }, [openDialog])

  const onAddWeightClick = useCallback(() => {
    openDialog({
      dialogType: dialogTypes.addDataCrossingWeight,
      title: 'DataCrossings.AddWeight',
      onAction: ({ attributeName, weight }) => editWeight(attributeName, weight)
    })
  }, [editWeight, openDialog])

  const clearDataCrossingData = useCallback(() => {
    // Delegating async function to a separate func to prevent dialog from hanging on screen
    const action = () => {
      context.api.dataCrossingService.clearDataCrossingDuplicates()
        .then(() => setWeightsUnlocked(true))
        .catch(() => openErrorDialog({
          customTitle: 'DataCrossings.ClearFailedTitle',
          customErrorText: 'DataCrossings.ClearFailed'
        }))
    }

    openConfirmDialog({
      customTitle: 'DataCrossings.ClearDuplicatesTitle',
      customConfirmText: 'DataCrossings.ClearDuplicates',
      onAction: () => action()
    })
  }, [])

  const onWeightDetailClick = useCallback((dataItem) => {
    openDialog({
      dialogType: dialogTypes.addDataCrossingWeight,
      title: 'DataCrossings.EditWeight',
      item: dataItem,
      readonly: !weightsUnlocked,
      onAction: ({ attributeName, weight }) => editWeight(attributeName, weight)
    })
  }, [editWeight, openDialog, weightsUnlocked])

  const onAttributeDelete = useCallback((id) => {
    context.api.dataCrossingService.setWeight({ id })
      .then(() => {
        setLastReload(new Date())
      }).catch(() => {
        openAlertDialog({ title: 'DataCrossings.SettingWeightErrorTitle', text: 'DataCrossings.SettingWeightError' })
      })
  }, [])

  const weightActions = useMemo(() => {
    return weightsUnlocked
      ? { onDetailClick: dataItem => onWeightDetailClick(dataItem), deleteAction: (item) => onAttributeDelete(item.id) }
      : {}
  }, [onAttributeDelete, onWeightDetailClick, weightsUnlocked])

  const unlockAllowedCombinations = useCallback(() => {
    const action = () => {
      context.api.dataCrossingService.deleteInvalidatedProblematicCombinations()
        .then(() => {
          setAllowedCombinationsUnlocked(true)
        })
        .catch(() => {
          openErrorDialog()
        })
    }

    openConfirmDialog({
      customTitle: 'DataCrossings.AllowedCombinationModificationTitle',
      customConfirmText: 'DataCrossings.AllowedCombinationModification',
      onAction: action
    })
  }, [])

  const editAllowedCombination = useCallback(({ firstRelatedPersonTypeId, secondRelatedPersonTypeId, id }) => {
    context.api.dataCrossingService.setAllowedCombination({ id, firstRelatedPersonTypeId, secondRelatedPersonTypeId })
      .then(() => setAllowedCombinationLastReload(new Date()))
      .catch(() => openErrorDialog({
        title: 'DataCrossings.AllowedCombinationModificationErrorTitle',
        text: 'DataCrossings.AllowedCombinationModificationError' }))
  }, [])

  const onAllowedCombinationDetailClick = useCallback(dataItem => {
    openDialog({
      dialogType: dialogTypes.addDataCrossingAllowedCombination,
      title: 'DataCrossings.EditAssociationFilterTitle',
      onAction: editAllowedCombination,
      item: dataItem
    })
  }, [editAllowedCombination, openDialog])

  const onAllowedCombinationAddClick = useCallback(() => {
    openDialog({
      dialogType: dialogTypes.addDataCrossingAllowedCombination,
      title: 'DataCrossings.AddAllowedCombinationTitle',
      onAction: editAllowedCombination
    })
  }, [editAllowedCombination, openDialog])

  const onAllowedCombinationDelete = useCallback(id => {
    context.api.dataCrossingService.deleteAllowedCombination(id)
      .then(() => setAllowedCombinationLastReload(new Date()))
      .catch(() => openAlertDialog({
        title: 'DataCrossings.DeletingAllowedCombinationErrorTitle',
        text: 'DataCrossings.DeletingAllowedCombinationError' }))
  }, [])

  const allowedCombinationsActions = useMemo(() => {
    return allowedCombinationsUnlocked
      ? { onDetailClick: dataItem => onAllowedCombinationDetailClick(dataItem), deleteAction: dataItem => onAllowedCombinationDelete(dataItem.id) }
      : {}
  }, [allowedCombinationsUnlocked, onAllowedCombinationDelete, onAllowedCombinationDetailClick])

  const onAllowedBanksSave = useCallback(() => {
    const action = () => {
      context.api.dataCrossingService.setAllowedBankStates({ allowedStates: allowedBankStates })
        .catch(error => { console.log(error) })
    }

    openConfirmDialog({
      customTitle: 'DataCrossings.SaveAllowedBanksTitle',
      customConfirmText: 'DataCrossings.SaveAllowedBanks',
      onAction: () => action()
    })
  }, [allowedBankStates])

  const onAllowedBanksChange = useCallback((e) => {
    setAllowedBankStates(e.value)
  }, [])

  return (
    <Main>
      <MainTop>
        <MainTopTitle>
          <Resx name='DataCrossings.Settings' />
        </MainTopTitle>
      </MainTop>
      <MainBody clear>
        <WhiteBox header='DataCrossings.ThresholdAndJobManagement'>
          <Row>
            {loadingThreshold
              ? <LoadingIndicator />
              : <Col lg='2'>
                <FormGroup>
                  <Label><Resx name='DataCrossings.ActualThreshold' /></Label>
                  <NumericInput defaultValue={threshold} onChange={setThreshold} min={20} max={100} percentage integerOnly />
                </FormGroup>
                <Button className={`btn ${buttonStyle.class}`} icon={buttonStyle.icon} onClick={onSaveClick}>
                  <Resx name='Common.Save' />
                </Button>
              </Col>
            }
            {loadingBankStates
              ? <LoadingIndicator />
              : <Col>
                <FormGroup>
                  <Label><Resx name='DataCrossings.AllowedBankStates' /></Label>
                  <MultiSelect
                    dataSource={cached.getBankStates}
                    value={allowedBankStates}
                    textField={locale === 'ar' ? 'name' : 'latinName'}
                    onChange={onAllowedBanksChange}
                  />
                </FormGroup>
                <Button className={`btn ${buttonStyle.class}`} icon={buttonStyle.icon} onClick={onAllowedBanksSave}>
                  <Resx name='Common.Save' />
                </Button>
              </Col>}
            <Col lg='6'>
              <FormGroup>
                <Label><Resx name='DataCrossings.Jobs' /></Label>
                <div>
                  <Button onClick={onDuplicatesClick} disabled={duplicitiesRunning}><Resx
                    name='DataCrossings.RunDuplicateProcedure' /></Button>
                          &nbsp;
                  <Button onClick={onNormalizationClick} disabled={normalizationRunning}><Resx
                    name='DataCrossings.RunNormalizationJob' /></Button>
                          &nbsp;
                  <Button onClick={onCheckCombinationsClick} disabled={checkCombinationsRunning}><Resx
                    name={'DataCrossings.CheckProblematicCombinations'} /></Button>
                </div>
              </FormGroup>
            </Col>
          </Row>
        </WhiteBox>
        <WhiteBox header='DataCrossings.WeightManagement' buttons={!weightsUnlocked &&
          <Button color='danger' icon='edit' onClick={clearDataCrossingData}><Resx name='Common.Edit' /></Button>}>
          <p>
            <Button color='primary' className='btn' onClick={onAddWeightClick} disabled={!weightsUnlocked}><Resx name='DataCrossings.AddWeight' /></Button>
          </p>
          <BsrsGrid dataSource={context.api.dataCrossingService.getWeights} lastReloadedAt={lastReload}>
            <Column field='attributeName' title={localize('DataCrossings.AttributeName')} />
            <Column field='weight' title={localize('DataCrossings.Weight')} filter='numeric' />
            <Column {...commandColumnProps({ ...weightActions })} />
          </BsrsGrid>
        </WhiteBox>
        <WhiteBox header='DataCrossings.AllowedCombinations' buttons={!allowedCombinationsUnlocked &&
          <Button color='danger' icon='edit' onClick={unlockAllowedCombinations}><Resx name='Common.Edit' /></Button>}>
          <Button color='primary' onClick={onAllowedCombinationAddClick} disabled={!allowedCombinationsUnlocked}>
            <Resx name='DataCrossings.AddAllowedCombination' /></Button>
          <BsrsGrid dataSource={context.api.dataCrossingService.getAllowedCombinations} lastReloadedAt={allowedCombinationLastReload}>
            {LookupNameColumn({ locale, field: 'firstRelatedPersonType', title: localize('DataCrossings.FirstRelation'), editable: false })}
            {LookupNameColumn({ locale, field: 'secondRelatedPersonType', title: localize('DataCrossings.SecondRelation'), editable: false })}
            <Column {...commandColumnProps({ ...allowedCombinationsActions })} />
          </BsrsGrid>
        </WhiteBox>
      </MainBody>
    </Main>
  )
}



// WEBPACK FOOTER //
// ./src/internal-app/pages/administration/dataCrossingSettings.js